import * as React from 'react'
import { useFormDynamicValuesValidationSchema } from 'utils/useFormDynamicValuesValidationSchema'
import * as yup from 'yup'
import { Formik, Form } from 'formik'
import ProposalForm from 'proposals/proposal/ProposalForm'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import SelectCompany, { CompanyType } from 'proposals/components/SelectCompany'
import Button from '@mui/material/Button'
import AddAttachments from 'proposals/attachments/AddAttachments'
import AddedDocumentsList from 'proposals/attachments/AddedDocumentsList'
import AddedImagesList from 'proposals/attachments/AddedImagesList'
import Alert from '@mui/material/Alert'
import { useAttachments } from 'proposals/attachments/useAttachments'
import { useUpdateProposal } from 'proposals/edit/queries/useProposalEditQuery'
import { Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { UnsavedChangesReporter } from 'proposals/proposal/useReportUnsavedChanges'

type ProposalEditFormContainerProps = {
  fields: Field[]
  proposal: ProposalType
  rfpLang: string
  region: string
  onDirtyChange?: (isDirty: boolean) => void
}

type FormValues = {
  person: {
    firstName: string
    lastName: string
    email: string
    phone: string
  }
  dynamicValues: {
    [x: string]: string
  }
}

const ProposalEditFormContainer: React.FC<ProposalEditFormContainerProps> = ({
  fields,
  proposal,
  rfpLang,
  region,
  onDirtyChange
}) => {
  const { t } = useTranslation()

  const [company, setCompany] = React.useState<CompanyType | null>({
    name: proposal.bidderCompanyName,
    businessId: proposal.bidderBusinessId
  })

  const { mutate: updateProposal, isPending: isUpdating, isError, isSuccess } = useUpdateProposal(proposal.id)

  const inputFields = proposal.fieldValues.filter((item) => item.type !== 'title')

  const dynamicFieldValues = inputFields.reduce((memo, item) => {
    memo[item.fieldId] = item.value
    return memo
  }, {} as Record<string, string>)

  const { validationSchema } = useFormDynamicValuesValidationSchema(fields)

  const schema = yup.object().shape({
    dynamicValues: validationSchema,
    person: yup.object().shape({
      email: yup.string().email('Virheellinen sähköposti').required('Sähköposti on pakollinen tieto')
    })
  })

  const {
    models: {
      images,
      imagesUploading,
      documents,
      documentsUploading,
      showUploadError,
      isImagesLoading,
      isDocumentsLoading
    },
    operations: { addDropzoneFiles, deleteImage, deleteDocument }
  } = useAttachments(proposal.contextToken)

  const isUploadingFiles = imagesUploading.length > 0 || documentsUploading.length > 0

  const selectCompany = React.useCallback(
    (value: CompanyType | null): void => {
      setCompany(value)
    },
    [setCompany]
  )

  const onSubmit = (values: FormValues): void => {
    const dynamicValues = Object.keys(values.dynamicValues).map((key) => ({
      fieldId: key,
      value: values.dynamicValues[key].toString()
    }))

    if (company === null || isUploadingFiles) {
      return
    }

    const contactPerson = {
      ...values.person,
      businessId: company.businessId,
      companyName: company.name
    }

    const request = {
      values: dynamicValues,
      contactPerson: contactPerson
    }

    updateProposal(request)
  }

  if (isError) {
    return <Navigate to={`/error`} replace />
  }

  if (isSuccess) {
    return <Navigate to={`/${proposal.id}/edit/success`} replace />
  }

  return (
    <Box sx={{ gridArea: 'form' }}>
      <Typography color="primary" variant="h6" gutterBottom>
        {t('submit-proposal')}
      </Typography>
      <Typography variant="body2" gutterBottom>
        {t('vendor-information')}
      </Typography>
      <SelectCompany selectCompany={selectCompany} selectedValue={company} region={region} />
      <Formik
        validationSchema={schema}
        initialValues={{
          person: {
            firstName: proposal.bidderContactFirstName,
            lastName: proposal.bidderContactLastName,
            email: proposal.bidderContactEmail,
            phone: proposal.bidderContactPhone
          },
          dynamicValues: {
            ...dynamicFieldValues
          }
        }}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {({ isValid, dirty }): React.ReactNode => (
          <Form>
            <UnsavedChangesReporter
              formikDirty={dirty}
              companyBusinessId={company?.businessId}
              initialBusinessId={proposal.bidderBusinessId}
              imageIds={images.map((image) => image.id)}
              documentIds={documents.map((document) => document.id)}
              attachmentsReady={!!proposal.contextToken && !isImagesLoading && !isDocumentsLoading}
              isUploadingFiles={isUploadingFiles}
              onDirtyChange={onDirtyChange}
            />
            <ProposalForm fields={fields} rfpLang={rfpLang} />
            <Button
              sx={{ color: 'common.white', mt: 2, mb: 1 }}
              variant="contained"
              color="primary"
              type="submit"
              disabled={!dirty || !isValid || company === null || isUploadingFiles || isUpdating}
            >
              {t('save')}
            </Button>
            <Typography variant="body2">Saat päivityksen jälkeen kopion tarjouksestasi sähköpostiin</Typography>
          </Form>
        )}
      </Formik>
      <Box sx={{ mt: 4 }}>
        <Typography color="primary" variant="h6" gutterBottom>
          {t('attachments')}
        </Typography>
        <AddAttachments addDropzoneFiles={addDropzoneFiles} />
        <Box sx={{ mt: 2, ml: 2 }}>
          <AddedDocumentsList
            documents={documents}
            documentsUploading={documentsUploading}
            deleteDocument={deleteDocument}
          />
          <AddedImagesList images={images} imagesUploading={imagesUploading} deleteImage={deleteImage} />
          {showUploadError && (
            <Alert sx={{ mt: 1, maxWidth: '650px' }} severity="error">
              {t('attachment-error-text')}
            </Alert>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default ProposalEditFormContainer
